import { Form, Formik, FormikHelpers, useFormikContext } from "formik";
import { useMemo, useCallback } from "react";
import * as Yup from "yup";
import { AutoComplete } from "@volca/ui/components/ui/autocomplete";
import { Button } from "@volca/ui/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@volca/ui/components/ui/dialog";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { IBusiness } from "@/business/business";
import {
  GoogleBusinessAccount,
  useGetGoogleBusinessAccounts,
  useGetGoogleBusinessLocations,
  useGetGoogleBusinessAccountLocations,
  useCreateGoogleBusinessAccountLocation,
} from "@/business/google-business-query";
import BusinessInfoPair from "./business-info-pair";

const validationSchema = Yup.object().shape({
  selectedAccount: Yup.string().required("Please select an account"),
  selectedLocation: Yup.string().required("Please select a location"),
  selectedLocationTitle: Yup.string(),
  accountSearchValue: Yup.string(),
  locationSearchValue: Yup.string(),
});

type FormValues = Yup.InferType<typeof validationSchema>;

interface GoogleBusinessConnectionModalProps {
  business: IBusiness;
  onClose: () => void;
}

const initialValues: FormValues = {
  selectedAccount: "",
  selectedLocation: "",
  selectedLocationTitle: "",
  accountSearchValue: "",
  locationSearchValue: "",
};

interface InnerFormProps {
  businessId: string;
  accounts: GoogleBusinessAccount[];
  accountsLoading: boolean;
  existingLocationNames: Set<string>;
  isCreating: boolean;
  onCancel: () => void;
}

const InnerForm = ({
  businessId,
  accounts,
  accountsLoading,
  existingLocationNames,
  isCreating,
  onCancel,
}: InnerFormProps) => {
  const { values, setFieldValue, isValid } = useFormikContext<FormValues>();

  const selectedAccount = useMemo(
    () => accounts.find((acc) => acc.name === values.selectedAccount),
    [accounts, values.selectedAccount],
  );

  const { data: locations = [], isLoading: locationsLoading } =
    useGetGoogleBusinessLocations(businessId, values.selectedAccount);

  const selectedLocation = useMemo(
    () => locations.find((loc) => loc.name === values.selectedLocation),
    [locations, values.selectedLocation],
  );

  const locationItems = useMemo(
    () =>
      locations
        .filter((location) => !existingLocationNames.has(location.name))
        .map((location) => ({
          value: location.name,
          label: location.storeCode
            ? `${location.title} (${location.storeCode})`
            : location.title,
        })),
    [locations, existingLocationNames],
  );

  const accountItems = useMemo(
    () =>
      accounts.map((account) => ({
        value: account.name,
        label: `${account.accountName} (${account.type})`,
      })),
    [accounts],
  );

  const handleAccountChange = useCallback(
    (value: string) => {
      setFieldValue("selectedAccount", value);
      setFieldValue("selectedLocation", "");
      setFieldValue("selectedLocationTitle", "");
      setFieldValue("locationSearchValue", "");
    },
    [setFieldValue],
  );

  const handleLocationChange = useCallback(
    (value: string) => {
      const location = locations.find((loc) => loc.name === value);
      setFieldValue("selectedLocation", value);
      setFieldValue("selectedLocationTitle", location?.title || "");
    },
    [locations, setFieldValue],
  );

  return (
    <Form className="grid gap-4">
      <Stack align="start" gap={4}>
        <Text>Select an account</Text>

        <AutoComplete
          label="Account name & type"
          required
          selectedValue={values.selectedAccount || ""}
          onSelectedValueChange={handleAccountChange}
          searchValue={values.accountSearchValue || ""}
          onSearchValueChange={(value) =>
            setFieldValue("accountSearchValue", value)
          }
          items={accountItems}
          placeholder="Select an account"
          isLoading={accountsLoading}
          fullWidth
        />

        <AutoComplete
          label="Location"
          required
          selectedValue={values.selectedLocation || ""}
          onSelectedValueChange={handleLocationChange}
          searchValue={values.locationSearchValue || ""}
          onSearchValueChange={(value) =>
            setFieldValue("locationSearchValue", value)
          }
          items={locationItems}
          placeholder="Select a location"
          isLoading={locationsLoading}
          disabled={!selectedAccount}
          fullWidth
        />

        {!!selectedLocation && (
          <>
            <BusinessInfoPair
              label="Store name"
              value={selectedLocation?.title || ""}
              isEditMode={false}
            />
            <BusinessInfoPair
              label="Store code"
              value={selectedLocation?.storeCode || ""}
              isEditMode={false}
            />
            <BusinessInfoPair
              label="Location"
              value={selectedLocation?.name || ""}
              isEditMode={false}
            />
          </>
        )}
      </Stack>

      <DialogFooter>
        <Button
          type="button"
          variant="secondary"
          onClick={() => onCancel()}
          disabled={isCreating}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={!isValid || isCreating}
          loading={isCreating}
        >
          Save
        </Button>
      </DialogFooter>
    </Form>
  );
};

export const GoogleBusinessConnectionModal = ({
  business,
  onClose,
}: GoogleBusinessConnectionModalProps) => {
  const { data: accounts = [], isLoading: accountsLoading } =
    useGetGoogleBusinessAccounts(business.id);

  const { data: existingLocations = [] } = useGetGoogleBusinessAccountLocations(
    business.id,
  );

  const { mutateAsync: createLocationMutation, isPending: isCreating } =
    useCreateGoogleBusinessAccountLocation(business.id);

  const handleSubmit = (
    values: FormValues,
    { resetForm }: FormikHelpers<FormValues>,
  ) =>
    createLocationMutation(
      {
        account: values.selectedAccount,
        location: values.selectedLocation,
        title: values.selectedLocationTitle || "",
      },
      {
        onSuccess: () => {
          resetForm();
          onClose();
        },
        onError: (error) => {
          console.error("Failed to create Google Business location:", error);
        },
      },
    );

  const existingLocationNames = useMemo(
    () => new Set(existingLocations.map((loc) => loc.location)),
    [existingLocations],
  );

  return (
    <Dialog
      open={true}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Connect to Google Business</DialogTitle>
        </DialogHeader>

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          <InnerForm
            businessId={business.id}
            accounts={accounts}
            accountsLoading={accountsLoading}
            existingLocationNames={existingLocationNames}
            isCreating={isCreating}
            onCancel={onClose}
          />
        </Formik>
      </DialogContent>
    </Dialog>
  );
};
